import cookie from 'react-cookie'

import { getDomain } from 'shared/tools/url-helper'

const FBP_COOKIE = '_fbp'
const FBC_COOKIE = '_fbc'
const ONE_YEAR = 365 * 24 * 60 * 60

export type FacebookClickIds = {
  fbp?: string
  fbc?: string
}

/**
 * Client params for Meta Conversion API attribution on payment endpoints.
 * `client_ip_address` is intentionally omitted — backend reads it from request headers.
 */
export type MetaConversionClientParams = FacebookClickIds & {
  client_user_agent?: string
}

function getCookieOptions() {
  return {
    maxAge: ONE_YEAR,
    domain: `.${getDomain(window.location.hostname)}`,
    path: '/',
  }
}

function normalizeCookieValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function getFbclidFromLocation(): string | null {
  if (typeof window === 'undefined') return null

  return new URLSearchParams(window.location.search).get('fbclid')
}

function fbcMatchesFbclid(fbc: string, fbclid: string): boolean {
  return fbc.endsWith(`.${fbclid}`)
}

/**
 * Reads `_fbp` / `_fbc` and, when `fbclid` is in the URL, persists `_fbc`
 * (without rewriting an existing cookie for the same click id).
 * Call only from payment / analytics paths — same scope as before.
 */
export function getFacebookClickIds(): FacebookClickIds {
  const fbp = normalizeCookieValue(cookie.load(FBP_COOKIE))
  const cookieFbc = normalizeCookieValue(cookie.load(FBC_COOKIE))
  const fbclid = getFbclidFromLocation()

  if (fbclid && typeof window !== 'undefined') {
    if (cookieFbc && fbcMatchesFbclid(cookieFbc, fbclid)) {
      return { fbp, fbc: cookieFbc }
    }

    const freshFbc = `fb.1.${Date.now()}.${fbclid}`
    cookie.save(FBC_COOKIE, freshFbc, getCookieOptions())
    return { fbp, fbc: freshFbc }
  }

  return { fbp, fbc: cookieFbc }
}

export function getClientUserAgent(): string | undefined {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return undefined
  }

  return navigator.userAgent
}

export function getMetaConversionClientParams(): MetaConversionClientParams {
  const { fbp, fbc } = getFacebookClickIds()
  const client_user_agent = getClientUserAgent()

  return {
    ...(fbp && { fbp }),
    ...(fbc && { fbc }),
    ...(client_user_agent && { client_user_agent }),
  }
}
